import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError, voidRecord } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Page, PageHeader, StatusBadge, Modal, Field, inputCls, PrimaryButton, EmptyState } from "@/components/lims";
import { Plus, Package, Trash, PencilSimple } from "@phosphor-icons/react";
import { canDeleteInventory } from "@/lib/roles";

const CATEGORIES = ["Reagent", "Consumable", "Kit", "Chemical", "Antibody", "Enzyme", "Media", "Other"];
const empty = { name: "", category: "Reagent", lot_number: "", quantity: 0, unit: "units", reorder_level: 0, expiry_date: "", supplier: "", location: "" };

export default function Inventory() {
  const { user } = useAuth();
  const canManage = canDeleteInventory(user);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/inventory");
    setItems(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...empty, ...it, expiry_date: it.expiry_date || "" }); setOpen(true); };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form, quantity: parseFloat(form.quantity) || 0, reorder_level: parseFloat(form.reorder_level) || 0, expiry_date: form.expiry_date || null };
    try {
      if (editing) { await api.put(`/inventory/${editing.id}`, payload); toast.success("Item updated"); }
      else { await api.post("/inventory", payload); toast.success("Item added"); }
      setOpen(false); load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  const remove = async (it) => {
    if (!(await voidRecord(`/inventory/${it.id}`, it.name))) return;
    toast.success("Item voided"); load();
  };

  return (
    <Page>
      <PageHeader
        title="Inventory"
        subtitle="Reagents, consumables and reorder alerts"
        action={<PrimaryButton data-testid="new-inventory-button" onClick={openNew}><Plus size={18} /> Add Item</PrimaryButton>}
      />

      <div className="bg-white rounded-lg border border-slate-200 overflow-x-auto">
        {loading ? (
          <div className="p-8 space-y-3">{[...Array(5)].map((_, i) => <div key={i} className="h-10 bg-slate-100 rounded animate-pulse" />)}</div>
        ) : items.length === 0 ? (
          <EmptyState icon={Package} title="No inventory items" subtitle="Add reagents and consumables to track stock" />
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-5 py-3 font-semibold">Name</th>
                <th className="px-5 py-3 font-semibold">Category</th>
                <th className="px-5 py-3 font-semibold">Lot</th>
                <th className="px-5 py-3 font-semibold text-right">Quantity</th>
                <th className="px-5 py-3 font-semibold">Expiry</th>
                <th className="px-5 py-3 font-semibold">Status</th>
                <th className="px-5 py-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} data-testid={`inventory-row-${it.name}`} className="border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors">
                  <td className="px-5 py-3 font-medium text-slate-900">{it.name}<div className="text-xs text-slate-400 font-normal">{it.supplier}</div></td>
                  <td className="px-5 py-3 text-slate-600">{it.category}</td>
                  <td className="px-5 py-3 font-mono text-xs text-slate-500">{it.lot_number || "—"}</td>
                  <td className="px-5 py-3 text-right text-slate-600">{it.quantity} {it.unit}<div className="text-[10px] text-slate-400">reorder ≤ {it.reorder_level}</div></td>
                  <td className="px-5 py-3 text-slate-500 text-xs">{it.expiry_date || "—"}</td>
                  <td className="px-5 py-3"><StatusBadge status={it.status} /></td>
                  <td className="px-5 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button data-testid={`edit-inventory-${it.name}`} onClick={() => openEdit(it)} className="p-1.5 rounded-md text-slate-400 hover:text-slate-900 hover:bg-slate-100 transition-colors"><PencilSimple size={16} /></button>
                      {canManage && <button onClick={() => remove(it)} className="p-1.5 rounded-md text-slate-400 hover:text-status-critical hover:bg-status-critical/10 transition-colors"><Trash size={16} /></button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit Item" : "Add Item"} testid="inventory-modal">
        <form onSubmit={submit} className="space-y-4">
          <Field label="Name"><input data-testid="inventory-name-input" required value={form.name} onChange={set("name")} className={inputCls} /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Category"><select value={form.category} onChange={set("category")} className={inputCls}>{CATEGORIES.map((c) => <option key={c}>{c}</option>)}</select></Field>
            <Field label="Lot number"><input value={form.lot_number} onChange={set("lot_number")} className={inputCls} /></Field>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <Field label="Quantity"><input type="number" step="any" value={form.quantity} onChange={set("quantity")} className={inputCls} /></Field>
            <Field label="Unit"><input value={form.unit} onChange={set("unit")} className={inputCls} /></Field>
            <Field label="Reorder ≤"><input type="number" step="any" value={form.reorder_level} onChange={set("reorder_level")} className={inputCls} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Expiry date"><input type="date" value={form.expiry_date} onChange={set("expiry_date")} className={inputCls} /></Field>
            <Field label="Supplier"><input value={form.supplier} onChange={set("supplier")} className={inputCls} /></Field>
          </div>
          <Field label="Location"><input value={form.location} onChange={set("location")} className={inputCls} placeholder="Shelf / cabinet" /></Field>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 rounded-md text-sm text-slate-600 hover:bg-slate-100 transition-colors">Cancel</button>
            <PrimaryButton data-testid="save-inventory-button" disabled={saving}>{editing ? "Save" : "Add"}</PrimaryButton>
          </div>
        </form>
      </Modal>
    </Page>
  );
}
